import React, { useCallback, useState } from 'react';
import { PeriodType } from '../../../api';
import { Player } from '../../../services/references';
import { LineupReason } from '../../../services/sessionsUpload';
import styled from 'styled-components';
import { TextField, IconButton, Tooltip } from '@material-ui/core';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import StopIcon from '@material-ui/icons/Stop';
import InjuryIcon from '@material-ui/icons/LocalHospitalOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import SelectSinglePlayer, { displayName } from '../../Util/SelectSinglePlayer';
import SelectSinglePeriod from '../../Util/SelectSinglePeriod';
import DeleteDialog from './DeleteDialog';
import ExitDialog from './ExitDialog';

export type TPlayer = Player;

export type TLineupPlayer = { id: string; name?: string | null };

export type TLineupEntry = {
  id: string;
  type?: string | null;
  player?: TLineupPlayer | null;
  substituted?: TLineupPlayer | null;
  periodIn?: string | null;
  periodOut?: string | null;
  inSeconds: number;
  outSeconds?: number | null;
  shirtNumber: number;
  reason?: string | null;
  inEventId?: number | null;
  outEventId?: number | null;
};

export const toLineupPlayer = (player: Player | null | undefined): TLineupPlayer | null =>
  player ? { id: player.id, name: displayName(player) } : null;

interface Props {
  lineupEntry: TLineupEntry;
  allPlayers: Array<string>;
  onChange: (newEntry: TLineupEntry, prevEntry: TLineupEntry) => void;
  onDeleteSubstitution: (entry: TLineupEntry) => void;
  onDeleteRedCard: (entry: TLineupEntry) => void;
  onAddExit: (out: TLineupEntry, reason: LineupReason, outSeconds: number, period: PeriodType) => void;
  onAddSubstitution: (out: TLineupEntry, outSeconds: number, period: PeriodType, playerIn: Player) => void;
  keeper: boolean;
  team?: string;
  periods: Array<PeriodType>;
}

const SessionLineupsPlayer: React.FunctionComponent<Props> = ({
  lineupEntry,
  allPlayers,
  periods,
  onChange,
  onDeleteRedCard,
  onDeleteSubstitution,
  onAddSubstitution,
  onAddExit,
  keeper,
  team,
}) => {
  const [openDelete, setOpenDelete] = useState(false);
  const [openExit, setOpenExit] = useState(false);
  const onChangeShirtNumber = useCallback(
    (event) => {
      onChange({ ...lineupEntry, shirtNumber: parseInt(event.target.value) }, lineupEntry);
    },
    [lineupEntry, onChange]
  );

  const onChangeMinute = useCallback(
    (event) => {
      onChange({ ...lineupEntry, inSeconds: parseInt(event.target.value) * 60 }, lineupEntry);
    },
    [lineupEntry, onChange]
  );

  const onChangePlayer = useCallback(
    (newplayer: Player | null) => {
      onChange({ 
        ...lineupEntry, 
        shirtNumber: newplayer?.lastKnownNumber || lineupEntry.shirtNumber, 
        player: toLineupPlayer(newplayer)
      }, lineupEntry);
    },
    [lineupEntry, onChange]
  );

  const onChangePeriod = useCallback(
    (period: PeriodType) => {
      onChange({ ...lineupEntry, periodIn: period }, lineupEntry);
    },
    [lineupEntry, onChange]
  );

  const onExit = useCallback(() => {
    setOpenExit(true);
  }, []);

  const onDelete = useCallback(() => {
    if (
      lineupEntry.inSeconds > 0 &&
      lineupEntry.outSeconds &&
      !lineupEntry.substituted &&
      (lineupEntry.reason === LineupReason.RedCard || lineupEntry.reason === LineupReason.Injury)
    ) {
      //This was a player that was substituted in and got a red card or permanent injury, ask which we want to delete
      setOpenDelete(true);
    } else if (lineupEntry.inSeconds) {
      onDeleteSubstitution(lineupEntry);
    } else if (lineupEntry.outSeconds && !lineupEntry.substituted) {
      onDeleteRedCard(lineupEntry);
    }
  }, [lineupEntry, onDeleteRedCard, onDeleteSubstitution]);

  const onCloseDelete = useCallback(() => {
    setOpenDelete(false);
  }, []);

  const onCloseExit = useCallback(() => {
    setOpenExit(false);
  }, []);

  const handleRedCard = useCallback(() => {
    onDeleteRedCard(lineupEntry);
    setOpenDelete(false);
  }, [lineupEntry, onDeleteRedCard]);

  const handleSubstitution = useCallback(() => {
    onDeleteSubstitution(lineupEntry);
    setOpenDelete(false);
  }, [lineupEntry, onDeleteSubstitution]);

  return (
    <PlayerContainer>
      <DeleteDialog
        open={openDelete}
        onClose={onCloseDelete}
        onDeleteRedCard={handleRedCard}
        onDeleteSubstitution={handleSubstitution}
      />
      <ExitDialog
        lineupEntry={lineupEntry}
        open={openExit}
        periods={periods}
        onClose={onCloseExit}
        onAddSubstitution={onAddSubstitution}
        onAddExit={onAddExit}
        team={team}
        allPlayers={allPlayers}
      />
      <Prefix>{keeper ? 'GK' : ''}</Prefix>
      <ShirtNumber>
        <TextField
          value={lineupEntry.shirtNumber}
          type="number"
          onChange={onChangeShirtNumber}
          fullWidth
          InputProps={{ inputProps: { min: 1, max: 99 } }}
        />
      </ShirtNumber>
      <PlayerSelect>
        <SelectSinglePlayer
          keeper={keeper}
          add
          exclude={allPlayers.filter((id) => id !== lineupEntry.player?.id)}
          team={team}
          value={lineupEntry.player?.id}
          onChange={onChangePlayer}
        />
      </PlayerSelect>
      <Icons>
        {lineupEntry.inSeconds > 0 && (
          <Tooltip title="Substituted in">
            <>
              <div style={{ display: 'flex', alignItems: 'center', paddingRight: '16px', paddingTop: '12px' }}>
                <ArrowDropDownIcon fontSize="large" htmlColor="#00ff00" />
                <Minute>
                  <TextField
                    value={Math.ceil((lineupEntry.inSeconds || 0) / 60)}
                    type="number"
                    fullWidth
                    onChange={onChangeMinute}
                    InputProps={{ inputProps: { min: 0, max: 300 } }}
                  />
                </Minute>
                <span>'</span>
              </div>
              <div style={{ width: '150px' }}>
                <SelectSinglePeriod periods={periods} value={(lineupEntry.periodIn as PeriodType) || undefined} onChange={onChangePeriod} />
              </div>
            </>
          </Tooltip>
        )}
        {lineupEntry.outSeconds !== undefined && lineupEntry.substituted && (
          <Tooltip title="Substituted off">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ArrowDropUpIcon fontSize="large" htmlColor="#ff0000" />
              <div>{Math.ceil((lineupEntry.outSeconds || 0) / 60)}'</div>
            </div>
          </Tooltip>
        )}
        {lineupEntry.outSeconds !== undefined && !lineupEntry.substituted && lineupEntry.reason === LineupReason.RedCard && (
          <Tooltip title="Red card">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <StopIcon fontSize="large" htmlColor="#ff0000" />
              <div>{Math.ceil((lineupEntry.outSeconds || 0) / 60)}'</div>
            </div>
          </Tooltip>
        )}
        {lineupEntry.outSeconds !== undefined && !lineupEntry.substituted && lineupEntry.reason === LineupReason.Injury && (
          <Tooltip title="Permanent injury">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <InjuryIcon color="disabled" />
              <div>{Math.ceil((lineupEntry.outSeconds || 0) / 60)}'</div>
            </div>
          </Tooltip>
        )}
        {!lineupEntry.periodOut && lineupEntry.player?.id !== '' && (
          <Tooltip title="Substituted or red card">
            <IconButton aria-label="exit-pitch" onClick={onExit}>
              <ExitToAppIcon color="disabled" />
            </IconButton>
          </Tooltip>
        )}
        {(lineupEntry.inSeconds > 0 || (lineupEntry.outSeconds && !lineupEntry.substituted)) && (
          <Tooltip title="Delete substitution or red card">
            <IconButton aria-label="delete" onClick={onDelete}>
              <DeleteIcon color="disabled" />
            </IconButton>
          </Tooltip>
        )}
      </Icons>
    </PlayerContainer>
  );
};

const Minute = styled.div`
  width: 40px;
  padding-top: 8px;
`;

const Prefix = styled.td`
  display: table-cell;
  width: 30px;
  padding-top: 38px;
  font-weight: 600;
  flex-grow: 0;
`;

const ShirtNumber = styled.td`
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 26px;
`;

const PlayerSelect = styled.td``;

const Icons = styled.td`
  display: flex;
  flex-direction: row;
  margin-top: 12px;
`;

const PlayerContainer = styled.tr`
  width: 100%;
`;

export default SessionLineupsPlayer;
